/**
 * Dashboard Data Store
 *
 * TTL-based cache for dashboard test run data.
 * Keyed by projectId (or 'general' for the all-projects view).
 * Survives in-app navigation — only expires after TTL or explicit invalidation.
 *
 * @module store/dashboard/dashboardDataStore
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import logger from '../../shared/services/logging/logger';

const CACHE_TTL = 90_000; // 90 seconds

export const useDashboardDataStore = create((set, get) => ({
  // { [projectId | 'general']: { data: Array, timestamp: number } }
  testRunsCache: {},

  /**
   * Store fetched test runs for a project.
   * @param {string|null} projectId
   * @param {Array} data
   */
  setTestRuns: (projectId, data) => {
    const key = projectId || 'general';
    logger.debug('Caching dashboard test runs', { key, count: data?.length });
    set(state => ({
      testRunsCache: {
        ...state.testRunsCache,
        [key]: { data, timestamp: Date.now() }
      }
    }));
  },

  /**
   * Return cached test runs if still fresh, null if expired or missing.
   * @param {string|null} projectId
   * @returns {Array|null}
   */
  getTestRuns: (projectId) => {
    const key = projectId || 'general';
    const cached = get().testRunsCache[key];
    if (!cached) return null;
    if (Date.now() - cached.timestamp > CACHE_TTL) return null;
    logger.debug('Using cached dashboard test runs', { key });
    return cached.data;
  },

  /**
   * Bust cache for one project (or all if projectId is null).
   * Call this after mutations (create/delete/edit a run).
   * @param {string|null} projectId
   */
  invalidateTestRuns: (projectId = null) => {
    if (projectId) {
      const key = projectId || 'general';
      logger.debug('Invalidating dashboard cache for project', { key });
      set(state => {
        const next = { ...state.testRunsCache };
        delete next[key];
        return { testRunsCache: next };
      });
    } else {
      logger.debug('Invalidating all dashboard test run cache');
      set({ testRunsCache: {} });
    }
  }
}));

/**
 * Stable reference to store actions (never triggers re-renders).
 */
export const useDashboardDataActions = () =>
  useDashboardDataStore(
    useShallow(state => ({
      setTestRuns: state.setTestRuns,
      getTestRuns: state.getTestRuns,
      invalidateTestRuns: state.invalidateTestRuns
    }))
  );
